/**
 * 認証切れで /login に飛ばされる直前の画面を覚えておき、ログイン後にそこへ戻す。
 *
 * 本番の行動分析(3日)では /home→/login 45回・/ads/banner→/login 19回が
 * 強制遷移で、ログイン後は一律 /home に着地するため作業中の画面が失われていた。
 *
 * 保存先は sessionStorage（タブを閉じたら捨てる。localStorage だと別日の
 * ログインで意図しない画面に飛ぶ）。値はアプリ内の相対パスだけを受け付ける。
 * 外部 URL を受け入れるとログイン直後に外部サイトへ飛ばせるオープンリダイレクト
 * になるため、保存時と読み出し時の両方で検証する。
 */
export const RETURN_TO_KEY = 'tasky_return_to';

/**
 * 戻り先にしないパス。
 * - `/login` `/forgot-password` `/reset-password` `/auth` `/invite`:
 *   そのパス自身とその配下（ログイン直後に戻ると堂々巡りになる）
 * - `/signup` `/admin`: 前方一致（`/signup/setup` などの登録フロー全体、
 *   および別の認証系である管理画面）
 */
const BLOCKED_SELF_OR_SUBTREE = [
  '/login',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/auth',
  '/invite',
];
const BLOCKED_PREFIXES = ['/signup', '/admin'];

/** パス部分だけを取り出す（クエリ・ハッシュは判定に使わない）。 */
const toPathname = (path: string): string => path.split('#')[0].split('?')[0];

/**
 * ログイン後の戻り先として使えるパスか。
 * アプリ内の相対パス（先頭 `/`）のみ許可し、プロトコル相対 `//` `/\` と
 * 認証・登録系の画面は拒否する。
 */
export const isReturnablePath = (path: string): boolean => {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (!path.startsWith('/')) return false;
  // `//evil.example.com` `/\evil.example.com` はブラウザが外部 URL として解釈する
  if (path.startsWith('//') || path.startsWith('/\\')) return false;

  const pathname = toPathname(path).toLowerCase();

  if (
    BLOCKED_SELF_OR_SUBTREE.some(
      (blocked) => pathname === blocked || pathname.startsWith(`${blocked}/`),
    )
  ) {
    return false;
  }

  return !BLOCKED_PREFIXES.some((blocked) => pathname.startsWith(blocked));
};

/**
 * 戻り先を保存する。戻り先にできないパスは無視する。
 * sessionStorage が使えない環境（プライベートブラウジング等）では黙って諦める。
 */
export const saveReturnTo = (path: string): void => {
  if (!isReturnablePath(path)) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // 戻り先の保存失敗でログイン導線を止めない
  }
};

/** 戻り先を捨てる（ログアウト時など）。 */
export const clearReturnTo = (): void => {
  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // 消せなくても実害はない
  }
};

/** 戻り先を読み出して消す。保存値が不正なら消したうえで null を返す。 */
export const consumeReturnTo = (): string | null => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }

  clearReturnTo();

  if (!stored || !isReturnablePath(stored)) return null;
  return stored;
};
